/**
 * DEMO-ONLY: Auto-Scheduling Demo Service
 *
 * Orchestrates execution and reset of integrated auto-scheduling demo scenarios.
 * Used only by routes under /integrated-auto-scheduling-demo and the
 * Demo Scenarios dialog. Not imported by production views.
 */

import { ApolloClient } from '@apollo/client';
import { addDays, addHours, startOfDay } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { SchedulerOptionsDocument } from '~/graphql/__generated__/graphql';
import orderBy from 'lodash/orderBy';
import {
  CareRequirement,
  saveDemoState,
  clearScenarioState,
  getDemoState,
  Scenario1State,
  Scenario2State,
  Scenario3State,
  getNextDemoPatient,
} from './demoStateManager';
import {
  MOCK_PATIENTS,
  CARE_PATTERNS,
  CarePattern,
  MockPatient,
  findRoutineVisitType,
  findDischargeVisitType,
  findAdmissionVisitType,
  getFirstServiceLine,
  findDemoCancellationReason,
} from './mockAutoSchedulingData';
import {
  CREATE_PATIENT,
  CREATE_EPISODE_MUTATION,
  UPDATE_EPISODE_MUTATION,
  GET_EPISODE_CARE_REQUIREMENTS_QUERY,
  SCHEDULE_VISIT_FROM_VISIT_REQUEST,
  GET_PATIENT_VISITS_DEMO,
  CANCEL_VISIT_MUTATION,
  GET_DEMO_PATIENTS,
  GET_PATIENT_WITH_VISITS,
} from '~/queries/autoSchedulingDemo';

export interface OrgConfig {
  visitTypes: Record<string, any>;
  serviceLines: Record<string, any>;
  visitCancellationReasons: Record<string, any>;
}

export interface ScenarioExecutionResult {
  success: boolean;
  patientId?: number;
  episodeId?: number;
  visitIds?: number[];
  error?: string;
  patientDetails?: {
    firstName: string;
    lastName: string;
    mrn: string;
  };
}

/**
 * Build care requirements for admission scenario
 */
function buildAdmissionCareRequirements(
  pattern: CarePattern,
  visitTypeId: number,
  serviceIds: number[],
  admissionDate: Date,
  expectedLOS: number,
  timezone: string
): any[] {
  const startDate = fromZonedTime(startOfDay(admissionDate), timezone);
  // rruleUntil is exclusive on the server side, so set UNTIL to start of the day *after*
  // the last care day so that the last day itself is included in the generated occurrences.
  const endDate = fromZonedTime(startOfDay(addDays(admissionDate, expectedLOS)), timezone);

  return pattern.timeWindows.map((window) => ({
    visitTypeId,
    serviceIds,
    duration: pattern.duration,
    rruleFreq: 'daily' as const,
    rruleInterval: 1,
    rruleDtstart: startDate.toISOString(),
    rruleUntil: endDate.toISOString(),
    rruleByweekday: null,
    earliestStartTime: window.earliestStart,
    latestStartTime: window.latestStart,
    minTimeFromLastVisit: null,
    maxTimeFromLastVisit: null,
  }));
}

/**
 * Build care requirements for discharge scenario
 *
 * rruleDtstart = TODAY in patient timezone (DATE column stores just the date).
 * The server's admissionDateKey filter removes calendar days equal to rruleDtstart,
 * so setting dtstart = today ensures the discharge day (tomorrow) passes the filter
 * and gets a visit scheduled on it.
 *
 * rruleUntil = DISCHARGE DAY in patient timezone. Daily from today through
 * discharge day generates exactly one scheduling day: the discharge day itself.
 *
 * Only the discharge visit type is included — no routine AM on discharge day.
 */
function buildDischargeDayCareRequirements(
  dischargeVisitTypeId: number,
  serviceIds: number[],
  dischargeDate: Date,
  timezone: string
): any[] {
  const todayDateKey = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const dischargeDayKey = formatInTimeZone(dischargeDate, timezone, 'yyyy-MM-dd');

  return [
    {
      visitTypeId: dischargeVisitTypeId,
      serviceIds,
      duration: 90,
      rruleFreq: 'daily' as const,
      rruleInterval: 1,
      rruleDtstart: `${todayDateKey}T00:00:00.000Z`,
      rruleUntil: `${dischargeDayKey}T00:00:00.000Z`,
      rruleByweekday: null,
      earliestStartTime: '15:00',
      latestStartTime: '18:00',
      minTimeFromLastVisit: null,
      maxTimeFromLastVisit: null,
    },
  ];
}

/**
 * Wait for visits to be created by polling
 */
async function waitForVisitsCreated(
  apolloClient: ApolloClient<any>,
  patientId: number,
  episodeId: number,
  expectedMinimumCount: number,
  maxWaitMs: number = 30000
): Promise<any[]> {
  const startTime = Date.now();
  const pollInterval = 500; // Poll every 500ms

  while (Date.now() - startTime < maxWaitMs) {
    const { data } = await apolloClient.query({
      query: GET_PATIENT_VISITS_DEMO,
      variables: { patientId },
      fetchPolicy: 'network-only', // Always fetch fresh data
    });

    // Filter visits by episodeId since query returns all visits
    const allVisits = data?.patientSingle?.visits || [];
    const visits = allVisits.filter((v: any) => v.episodeId === episodeId);

    if (visits.length >= expectedMinimumCount) {
      console.log(`✓ Found ${visits.length} visits for episode (expected ${expectedMinimumCount})`);
      return visits;
    }

    console.log(`Polling for visits... found ${visits.length}/${expectedMinimumCount} for episode ${episodeId}`);
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  throw new Error(
    `Timeout waiting for visits to be created. Expected at least ${expectedMinimumCount} visits.`
  );
}

/**
 * Scenario 1: Admission Order
 *
 * Creates a new patient, activates episode, and auto-schedules all visits.
 */
export async function executeAdmissionScenario(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  mockPatient: MockPatient,
  carePattern: CarePattern,
  expectedLOS: number,
  onProgress?: (step: string) => void
): Promise<ScenarioExecutionResult> {
  try {
    onProgress?.('Simulating EHR order...');

    // Get next unique demo patient (with iteration and timestamp)
    // Pass true to increment counter in live mode
    const demoPatient = getNextDemoPatient(MOCK_PATIENTS, true);

    // Resolve visit type and service IDs from org config
    const visitTypeId = findRoutineVisitType(orgConfig.visitTypes);
    const serviceLineId = getFirstServiceLine(orgConfig.serviceLines);

    console.log('Debug - visitTypeId:', visitTypeId);
    console.log('Debug - serviceLineId:', serviceLineId);
    console.log('Debug - orgConfig.visitTypes:', Object.keys(orgConfig.visitTypes || {}));
    console.log('Debug - orgConfig.serviceLines:', Object.keys(orgConfig.serviceLines || {}));

    if (!visitTypeId || !serviceLineId) {
      const missingItems = [];
      if (!visitTypeId) missingItems.push('visit type');
      if (!serviceLineId) missingItems.push('service line');
      throw new Error(
        `Could not find required ${missingItems.join(' and ')}. Please configure your organization first.`
      );
    }

    // Use empty serviceIds array - services can be added later
    // This avoids the GraphQL codegen issue with GET_ALL_SERVICES
    const serviceIds: number[] = [];

    onProgress?.('Creating patient...');

    // Create patient with unique data
    const { data: patientData } = await apolloClient.mutate({
      mutation: CREATE_PATIENT,
      variables: {
        firstName: demoPatient.firstName,
        lastName: demoPatient.lastName,
        phone: demoPatient.phone,
        dateOfBirth: demoPatient.dateOfBirth,
        addressStreet: demoPatient.addressStreet,
        addressLineTwo: demoPatient.addressLineTwo || undefined,
        addressCity: demoPatient.addressCity,
        addressState: demoPatient.addressState,
        addressZip: demoPatient.addressZip,
        sex: undefined,
        patientNotes: demoPatient.patientNotes || undefined,
        serviceInstructions: undefined,
        patientIdentifiers: [
          {
            identifier: demoPatient.mrn,
            identifier_type: 'MRN',
          },
        ],
        addressPlaceId: undefined,
      },
    });

    const patientId = patientData.createPatient.id;
    const timezone = patientData.createPatient.addressTimezone || 'America/New_York';

    console.log(`✓ Patient created: ${patientId}`);
    onProgress?.('Activating episode...');

    // Store dates for later use - will add care requirements after admission is coordinated
    const admissionDate = new Date();
    const tomorrowDate = addDays(admissionDate, 1); // Start from tomorrow
    const expectedDischargeDate = addDays(admissionDate, expectedLOS);

    console.log(`[executeAdmissionScenario] Setting episode dates:`, {
      admissionDate: admissionDate.toISOString(),
      expectedDischargeDate: expectedDischargeDate.toISOString(),
      expectedLOS,
    });

    // Create episode WITHOUT care requirements initially
    // Care requirements will be added after admission visit is coordinated
    // This prevents auto-creation of provisional visits before coordination
    console.log('[executeAdmissionScenario] Creating episode WITHOUT care requirements (will add after admission coordination)');

    const { data: episodeData} = await apolloClient.mutate({
      mutation: CREATE_EPISODE_MUTATION,
      variables: {
        patientId,
        serviceLineId,
        careRequirements: [], // Empty - will add after admission coordination
      },
    });

    const episodeId = episodeData.createEpisode.id;
    console.log(`✓ Episode created: ${episodeId}`);
    console.log('Episode data returned from mutation:', episodeData.createEpisode);

    onProgress?.('Auto-scheduling visits for tomorrow onwards...');

    // Visits for tomorrow onwards will be auto-created
    // Today's visits will be created through admission coordination dialog
    const visitIds: number[] = [];

    onProgress?.(`✓ Complete! Tomorrow's visits auto-scheduled. Ready to coordinate today's admission visit.`);

    // Save demo state with episode details
    const scenario1State: Scenario1State = {
      patientId,
      episodeId,
      visitIds,
      executedAt: new Date().toISOString(),
      episodeDetails: {
        id: episodeId,
        admissionDate: admissionDate.toISOString(),
        expectedDischargeDate: expectedDischargeDate.toISOString(),
        currentDay: 1,
        expectedLOS,
        carePattern: {
          name: carePattern.name,
          visitsPerDay: carePattern.visitsPerDay,
          duration: carePattern.duration,
        },
      },
    };
    saveDemoState('scenario1', scenario1State);

    return {
      success: true,
      patientId,
      episodeId,
      visitIds,
      patientDetails: {
        firstName: demoPatient.firstName,
        lastName: demoPatient.lastName,
        mrn: demoPatient.mrn,
      },
    };
  } catch (error: any) {
    console.error('Admission scenario failed:', error);
    onProgress?.(`✗ Error: ${error.message}`);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Create admission visit and optional today's routine visits
 */
export interface ProviderResource {
  providerId: number;
  providerTypeId?: number;
}

export async function createAdmissionVisits(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  patientId: number,
  episodeId: number,
  serviceLineId: number,
  admissionTime: string,
  providerResources: ProviderResource[],
  autoScheduleToday: boolean,
  carePattern: CarePattern,
  selectedVisitTypeId: number | null,
  onProgress?: (step: string) => void,
  expectedLOS?: number
): Promise<{ admissionVisitId: number; todaysVisitIds: number[] }> {
  try {
    // Use selected visit type or fall back to finding admission visit type
    const admissionVisitTypeId = selectedVisitTypeId || findAdmissionVisitType(orgConfig.visitTypes);
    const routineVisitType = findRoutineVisitType(orgConfig.visitTypes);

    if (!admissionVisitTypeId || !routineVisitType) {
      throw new Error('Could not find required visit types');
    }

    // Get patient timezone
    const { data: patientData } = await apolloClient.query({
      query: GET_PATIENT_WITH_VISITS,
      variables: { patientId },
      fetchPolicy: 'network-only',
    });

    const timezone = patientData.patientSingle?.addressTimezone || 'America/New_York';

    // Use empty serviceIds array - services can be added later
    const serviceIds: number[] = [];

    onProgress?.('Creating admission visit...');

    // Create admission visit (90 minutes)
    // Strip out providerTypeId - mutation only expects providerId
    const resources = providerResources.map(({ providerId }) => ({ providerId }));

    const { data: admissionData } = await apolloClient.mutate({
      mutation: SCHEDULE_VISIT_FROM_VISIT_REQUEST,
      variables: {
        patientId,
        serviceLineId,
        visitTypeId: admissionVisitTypeId,
        startTime: admissionTime,
        duration: 90,
        serviceIds,
        resources,
      },
    });

    const admissionVisitId = admissionData.scheduleOverrideVisit.id;
    console.log(`✓ Admission visit created: ${admissionVisitId}`);

    // Now add care requirements to the episode
    // This will auto-create routine visits for the full LOS (starting tomorrow)
    if (expectedLOS && expectedLOS > 1) {
      onProgress?.('Auto-scheduling visits for expected length of stay...');

      const todayInTz = toZonedTime(new Date(), timezone);
      const tomorrowDate = fromZonedTime(addDays(startOfDay(todayInTz), 1), timezone);

      const careRequirements = buildAdmissionCareRequirements(
        carePattern,
        routineVisitType,
        serviceIds,
        tomorrowDate, // Start from tomorrow
        expectedLOS - 1, // Days 2–LOS; day 1 (today) is handled separately via coordination
        timezone
      );

      console.log(`[createAdmissionVisits] Adding care requirements to episode ${episodeId}`, {
        requirementsCount: careRequirements.length,
        startDate: tomorrowDate.toISOString(),
        daysOfCare: expectedLOS - 1,
      });

      await apolloClient.mutate({
        mutation: UPDATE_EPISODE_MUTATION,
        variables: {
          episodeId,
          careRequirements,
        },
      });

      console.log(`✓ Care requirements added - routine visits auto-created for ${expectedLOS - 1} days`);
    }

    const todaysVisitIds: number[] = [];

    if (autoScheduleToday) {
      onProgress?.('Scheduling today\'s routine visits...');

      const admissionDate = new Date(admissionTime);
      const admissionHour = parseInt(formatInTimeZone(admissionDate, timezone, 'H'), 10);

      // Filter to visit windows after admission time
      const remainingWindows = carePattern.timeWindows.filter((window) => {
        const [windowHour] = window.earliestStart.split(':').map(Number);
        return windowHour > admissionHour;
      });

      // Create visits for remaining windows — run optimizer per window to avoid provider conflicts
      for (const window of remainingWindows) {
        const [hours, minutes] = window.earliestStart.split(':').map(Number);
        const visitStart = new Date(admissionDate);
        visitStart.setHours(hours, minutes, 0, 0);

        // Ask the optimizer for the best available option at this window's target time
        let windowResources = resources; // fallback: admission visit providers
        try {
          const { data: optionsData } = await apolloClient.query({
            query: SchedulerOptionsDocument,
            variables: {
              patientId,
              startTime: visitStart.toISOString(),
              targetTime: visitStart.toISOString(),
              endTime: addHours(visitStart, 4).toISOString(),
              serviceLineId,
              visitTypeId: routineVisitType,
              duration: carePattern.duration,
              serviceIds,
              providerTraits: [],
              daysOfWeek: null,
              hoursOfDay: null,
              reschedulingVisitId: null,
              selectedProviderIds: null,
              useOverflowShifts: false,
            },
            fetchPolicy: 'network-only',
          });

          const opts = optionsData?.scheduleOption?.options ?? [];
          const best = orderBy(opts, ['scores.totalScore', (o: any) => new Date(o.startTime)], ['desc', 'asc'])[0];
          if (best?.resources?.length) {
            windowResources = best.resources.map((r: any) => ({ providerId: r.providerId }));
            visitStart.setTime(new Date(best.startTime).getTime());
          }
        } catch (err) {
          console.warn('[createAdmissionVisits] Optimizer query failed for window, falling back to admission providers', err);
        }

        const { data: visitData } = await apolloClient.mutate({
          mutation: SCHEDULE_VISIT_FROM_VISIT_REQUEST,
          variables: {
            patientId,
            serviceLineId,
            visitTypeId: routineVisitType,
            startTime: visitStart.toISOString(),
            duration: carePattern.duration,
            serviceIds,
            resources: windowResources,
          },
        });

        todaysVisitIds.push(visitData.scheduleOverrideVisit.id);
      }

      onProgress?.(`✓ Created ${todaysVisitIds.length} additional visits for today`);
    }

    return { admissionVisitId, todaysVisitIds };
  } catch (error: any) {
    console.error('Failed to create admission visits:', error);
    throw error;
  }
}

/**
 * Scenario 2: Pattern Change Order
 *
 * Cancels all future visits, reduces care pattern to 1x daily, shortens LOS by 1.
 */
export async function executePatternChangeScenario(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  patientId: number,
  episodeId: number,
  admissionDateStr: string,
  expectedLOS: number,
  onProgress?: (step: string) => void
): Promise<ScenarioExecutionResult & { patientName?: string; canceledCount?: number; newCount?: number }> {
  try {
    onProgress?.('Simulating EHR order...');

    // Get current patient data
    const { data: patientData } = await apolloClient.query({
      query: GET_PATIENT_VISITS_DEMO,
      variables: { patientId },
      fetchPolicy: 'network-only',
    });

    const patient = patientData.patientSingle;
    const timezone = patient.addressTimezone || 'America/New_York';
    const allVisits: any[] = patient.visits || [];
    const patientName = `${patient.firstName} ${patient.lastName}`;

    // Get original care requirements for backup
    const { data: careReqData } = await apolloClient.query({
      query: GET_EPISODE_CARE_REQUIREMENTS_QUERY,
      variables: { episodeId },
      fetchPolicy: 'network-only',
    });
    const originalCareRequirements = careReqData.careRequirement || [];

    // Cancel all future visits (tomorrow onwards) that are not already canceled
    const cancellationReasonId = findDemoCancellationReason(orgConfig.visitCancellationReasons);
    if (!cancellationReasonId) {
      throw new Error('Could not find cancellation reason.');
    }

    const tomorrowStart = startOfDay(addDays(new Date(), 1));
    const futureVisits = allVisits.filter(
      (v: any) =>
        v.episodeId === episodeId &&
        v.eventStatus !== 'canceled' &&
        new Date(v.startTime) >= tomorrowStart
    );

    onProgress?.(`Canceling ${futureVisits.length} future visits...`);

    for (const visit of futureVisits) {
      await apolloClient.mutate({
        mutation: CANCEL_VISIT_MUTATION,
        variables: {
          visitId: visit.id,
          visitCancellationReasonId: cancellationReasonId,
        },
      });
    }

    onProgress?.('Updating pattern of care...');

    const visitTypeId = findRoutineVisitType(orgConfig.visitTypes);
    if (!visitTypeId) {
      throw new Error('Could not find routine visit type.');
    }

    const serviceIds: number[] = [];
    const newCarePattern = CARE_PATTERNS.singleDaily;

    // Remaining days = LOS - 2 (shorten LOS by 1, exclude today)
    // e.g., LOS=6 → 4 remaining days (days 2–5 of shortened 5-day LOS)
    const todayInTz = toZonedTime(new Date(), timezone);
    const tomorrowDate = fromZonedTime(addDays(startOfDay(todayInTz), 1), timezone);
    const remainingDays = expectedLOS - 2;

    const newCareRequirements = buildAdmissionCareRequirements(
      newCarePattern,
      visitTypeId,
      serviceIds,
      tomorrowDate,
      remainingDays,
      timezone
    );

    await apolloClient.mutate({
      mutation: UPDATE_EPISODE_MUTATION,
      variables: {
        episodeId,
        careRequirements: newCareRequirements,
      },
    });

    onProgress?.('Scheduling new visits...');

    const expectedNewVisitCount = newCarePattern.visitsPerDay * remainingDays;
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const visits = await waitForVisitsCreated(
      apolloClient,
      patientId,
      episodeId,
      expectedNewVisitCount,
      60000
    );

    // Identify new visit IDs (those not in the original canceled set)
    const canceledVisitIds = futureVisits.map((v: any) => v.id);
    const canceledIdSet = new Set(canceledVisitIds);
    const newVisitIds = visits
      .filter((v: any) => !canceledIdSet.has(v.id) && v.eventStatus !== 'canceled')
      .map((v: any) => v.id);

    onProgress?.(
      `✓ Complete! ${canceledVisitIds.length} visits canceled, ${newVisitIds.length} new visits scheduled`
    );

    const scenario2State: Scenario2State = {
      patientId,
      episodeId,
      originalCareRequirements,
      canceledVisitIds,
      newVisitIds,
      executedAt: new Date().toISOString(),
    };
    saveDemoState('scenario2', scenario2State);

    return {
      success: true,
      patientId,
      episodeId,
      visitIds: newVisitIds,
      patientName,
      canceledCount: canceledVisitIds.length,
      newCount: newVisitIds.length,
    };
  } catch (error: any) {
    console.error('Pattern change scenario failed:', error);
    onProgress?.(`✗ Error: ${error.message}`);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Scenario 3: Discharge Order
 *
 * Updates pattern for discharge day with routine + discharge visits.
 */
export async function executeDischargeScenario(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  patientId: number,
  episodeId: number,
  dischargeDate: Date,
  onProgress?: (step: string) => void
): Promise<ScenarioExecutionResult & { patientName?: string; canceledCount?: number; dischargeDate?: string }> {
  try {
    onProgress?.('Simulating EHR order...');

    // Get current episode data
    const { data: patientData } = await apolloClient.query({
      query: GET_PATIENT_VISITS_DEMO,
      variables: { patientId, episodeId },
      fetchPolicy: 'network-only',
    });

    const patient = patientData.patientSingle;
    const patientName = `${patient.firstName} ${patient.lastName}`;
    const episode = patient.episodes?.[0];
    const timezone = patient.addressTimezone || 'America/New_York';
    const allVisits: any[] = patient.visits || [];

    if (!episode) {
      throw new Error('Episode not found for this patient.');
    }

    // Count future visits (on or after discharge date) that will be replaced
    const dischargeDateStart = startOfDay(dischargeDate);
    const futureVisits = allVisits.filter(
      (v: any) =>
        v.episodeId === episodeId &&
        v.eventStatus !== 'canceled' &&
        new Date(v.startTime) >= dischargeDateStart
    );
    const canceledCount = futureVisits.length;

    // Get original care requirements for backup
    const { data: careReqData } = await apolloClient.query({
      query: GET_EPISODE_CARE_REQUIREMENTS_QUERY,
      variables: { episodeId },
      fetchPolicy: 'network-only',
    });

    const originalCareRequirements = careReqData.careRequirement || [];

    onProgress?.('Updating pattern of care...');

    // Only the discharge visit type is needed
    const dischargeVisitTypeId = findDischargeVisitType(orgConfig.visitTypes);

    if (!dischargeVisitTypeId) {
      throw new Error('Could not find discharge visit type.');
    }

    // Use empty serviceIds array - services can be added later
    const serviceIds: number[] = [];

    // Build discharge day care requirements (single discharge visit, today→discharge day date range)
    const dischargeDayCareRequirements = buildDischargeDayCareRequirements(
      dischargeVisitTypeId,
      serviceIds,
      dischargeDate,
      timezone
    );

    // Replace all care requirements with just the discharge requirement.
    // rruleDtstart = today anchors the displayed admission date to today,
    // and the service's admissionDateKey filter removes today so the discharge
    // day (tomorrow) gets the visit. Old requirements not in the list are deleted
    // by the service, and cancelOrphanedAutoscheduledVisits cleans up their visits.
    await apolloClient.mutate({
      mutation: UPDATE_EPISODE_MUTATION,
      variables: {
        episodeId,
        careRequirements: dischargeDayCareRequirements,
      },
    });

    console.log(`✓ Episode updated with discharge pattern`);

    onProgress?.('Updating final day visits...');

    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Get updated visits
    const { data: updatedData } = await apolloClient.query({
      query: GET_PATIENT_VISITS_DEMO,
      variables: { patientId, episodeId },
      fetchPolicy: 'network-only',
    });

    const updatedVisits = updatedData.patientSingle.visits || [];
    const updatedVisitIds = updatedVisits.map((v: any) => v.id);

    onProgress?.(`✓ Complete! ${updatedVisits.length} visits updated`);

    // Save demo state
    const scenario3State: Scenario3State = {
      patientId,
      episodeId,
      originalCareRequirements,
      updatedVisitIds,
      executedAt: new Date().toISOString(),
    };
    saveDemoState('scenario3', scenario3State);

    return {
      success: true,
      patientId,
      episodeId,
      visitIds: updatedVisitIds,
      patientName,
      canceledCount,
      dischargeDate: dischargeDate.toISOString(),
    };
  } catch (error: any) {
    console.error('Discharge scenario failed:', error);
    onProgress?.(`✗ Error: ${error.message}`);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Reset Scenario 1: Delete admission demo patient
 */
export async function resetAdmissionScenario(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  patientId: number,
  episodeId: number,
  visitIds: number[],
  onProgress?: (step: string) => void
): Promise<void> {
  try {
    onProgress?.('Resetting scenario...');

    // Get cancellation reason
    const cancellationReasonId = findDemoCancellationReason(
      orgConfig.visitCancellationReasons
    );

    if (!cancellationReasonId) {
      throw new Error('Could not find cancellation reason for demo reset.');
    }

    onProgress?.('Canceling visits...');

    // Cancel all visits
    await Promise.all(
      visitIds.map((visitId) =>
        apolloClient.mutate({
          mutation: CANCEL_VISIT_MUTATION,
          variables: { visitId, visitCancellationReasonId: cancellationReasonId },
        }).catch((err) => {
          // Visit might already be canceled or deleted
          console.warn(`Could not cancel visit ${visitId}:`, err.message);
        })
      )
    );

    console.log(`✓ Canceled ${visitIds.length} visits`);

    onProgress?.('Deactivating episode...');

    // Deactivate episode
    await apolloClient.mutate({
      mutation: UPDATE_EPISODE_MUTATION,
      variables: {
        episodeId,
        active: false,
        careRequirements: [],
      },
    }).catch((err) => {
      console.warn(`Could not deactivate episode ${episodeId}:`, err.message);
    });

    console.log(`✓ Episode deactivated`);

    onProgress?.('Clearing demo state...');
    clearScenarioState('scenario1');

    onProgress?.('✓ Reset complete');
  } catch (error: any) {
    console.error('Reset admission scenario failed:', error);
    throw new Error(`Failed to reset: ${error.message}`);
  }
}

/**
 * Reset Scenario 2: Revert pattern change
 */
export async function resetPatternChangeScenario(
  apolloClient: ApolloClient<any>,
  patientId: number,
  episodeId: number,
  originalCareRequirements: any[],
  onProgress?: (step: string) => void
): Promise<void> {
  try {
    onProgress?.('Resetting scenario...');

    onProgress?.('Restoring original pattern...');

    // Restore original care requirements
    const careRequirementsToRestore = originalCareRequirements.map((cr) => ({
      id: cr.id,
      visitTypeId: cr.visitTypeId,
      duration: cr.duration,
      rruleFreq: cr.rruleFreq,
      rruleDtstart: cr.rruleDtstart,
      rruleUntil: cr.rruleUntil,
      rruleInterval: cr.rruleInterval,
      rruleByweekday: cr.rruleByweekday,
      earliestStartTime: cr.earliestStartTime,
      latestStartTime: cr.latestStartTime,
      minTimeFromLastVisit: cr.minTimeFromLastVisit,
      maxTimeFromLastVisit: cr.maxTimeFromLastVisit,
      serviceIds: cr.services?.map((s: any) => s.serviceId) || [],
    }));

    await apolloClient.mutate({
      mutation: UPDATE_EPISODE_MUTATION,
      variables: {
        episodeId,
        careRequirements: careRequirementsToRestore,
      },
    });

    console.log(`✓ Original pattern restored`);

    onProgress?.('Clearing demo state...');
    clearScenarioState('scenario2');

    onProgress?.('✓ Reset complete');
  } catch (error: any) {
    console.error('Reset pattern change scenario failed:', error);
    throw new Error(`Failed to reset: ${error.message}`);
  }
}

/**
 * Reset Scenario 3: Revert discharge changes
 */
export async function resetDischargeScenario(
  apolloClient: ApolloClient<any>,
  patientId: number,
  episodeId: number,
  originalCareRequirements: any[],
  onProgress?: (step: string) => void
): Promise<void> {
  try {
    onProgress?.('Resetting scenario...');

    onProgress?.('Reverting discharge changes...');

    // Restore original care requirements
    const careRequirementsToRestore = originalCareRequirements.map((cr) => ({
      id: cr.id,
      visitTypeId: cr.visitTypeId,
      duration: cr.duration,
      rruleFreq: cr.rruleFreq,
      rruleDtstart: cr.rruleDtstart,
      rruleUntil: cr.rruleUntil,
      rruleInterval: cr.rruleInterval,
      rruleByweekday: cr.rruleByweekday,
      earliestStartTime: cr.earliestStartTime,
      latestStartTime: cr.latestStartTime,
      minTimeFromLastVisit: cr.minTimeFromLastVisit,
      maxTimeFromLastVisit: cr.maxTimeFromLastVisit,
      serviceIds: cr.services?.map((s: any) => s.serviceId) || [],
    }));

    await apolloClient.mutate({
      mutation: UPDATE_EPISODE_MUTATION,
      variables: {
        episodeId,
        careRequirements: careRequirementsToRestore,
      },
    });

    console.log(`✓ Discharge changes reverted`);

    onProgress?.('Clearing demo state...');
    clearScenarioState('scenario3');

    onProgress?.('✓ Reset complete');
  } catch (error: any) {
    console.error('Reset discharge scenario failed:', error);
    throw new Error(`Failed to reset: ${error.message}`);
  }
}

/**
 * Reset all scenarios
 */
export async function resetAllScenarios(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  onProgress?: (step: string) => void
): Promise<void> {
  const state = getDemoState();
  const errors: string[] = [];

  // Reset Scenario 1
  if (state.scenario1) {
    try {
      await resetAdmissionScenario(
        apolloClient,
        orgConfig,
        state.scenario1.patientId,
        state.scenario1.episodeId,
        state.scenario1.visitIds,
        onProgress
      );
    } catch (error: any) {
      errors.push(`Scenario 1: ${error.message}`);
    }
  }

  // Reset Scenario 2
  if (state.scenario2) {
    try {
      await resetPatternChangeScenario(
        apolloClient,
        state.scenario2.patientId,
        state.scenario2.episodeId,
        state.scenario2.originalCareRequirements,
        onProgress
      );
    } catch (error: any) {
      errors.push(`Scenario 2: ${error.message}`);
    }
  }

  // Reset Scenario 3
  if (state.scenario3) {
    try {
      await resetDischargeScenario(
        apolloClient,
        state.scenario3.patientId,
        state.scenario3.episodeId,
        state.scenario3.originalCareRequirements,
        onProgress
      );
    } catch (error: any) {
      errors.push(`Scenario 3: ${error.message}`);
    }
  }

  if (errors.length > 0) {
    throw new Error(`Some scenarios failed to reset:\n${errors.join('\n')}`);
  }

  onProgress?.('✓ All scenarios reset successfully');
}

/**
 * Get all demo patients from scenarios
 */
export async function getDemoPatients(
  apolloClient: ApolloClient<any>
): Promise<any[]> {
  const state = getDemoState();
  const patientIds = new Set<number>();

  // Collect all unique patient IDs from scenarios
  if (state.scenario1?.patientId) patientIds.add(state.scenario1.patientId);
  if (state.scenario2?.patientId) patientIds.add(state.scenario2.patientId);
  if (state.scenario3?.patientId) patientIds.add(state.scenario3.patientId);

  if (patientIds.size === 0) {
    return [];
  }

  try {
    const { data } = await apolloClient.query({
      query: GET_DEMO_PATIENTS,
      variables: { patientIds: Array.from(patientIds) },
      fetchPolicy: 'network-only',
    });

    return data.patient || [];
  } catch (error: any) {
    console.error('Failed to fetch demo patients:', error);
    throw new Error(`Failed to fetch demo patients: ${error.message}`);
  }
}

/**
 * Cancel all visits for specified patients
 */
export async function cancelVisitsForPatients(
  apolloClient: ApolloClient<any>,
  orgConfig: OrgConfig,
  patientIds: number[],
  onProgress?: (step: string) => void
): Promise<void> {
  if (patientIds.length === 0) return;

  try {
    onProgress?.('Finding visits to cancel...');

    // Get cancellation reason
    const cancellationReasonId = findDemoCancellationReason(
      orgConfig.visitCancellationReasons
    );

    if (!cancellationReasonId) {
      throw new Error('Could not find cancellation reason for demo.');
    }

    // Get all visits and episodes for each patient
    const visitsToCancel: number[] = [];
    const episodesToDeactivate: number[] = [];

    for (const patientId of patientIds) {
      const { data } = await apolloClient.query({
        query: GET_PATIENT_WITH_VISITS,
        variables: { patientId },
        fetchPolicy: 'network-only',
      });

      const visits = (data?.visit || []).filter((v: any) => v.eventStatus !== 'canceled');
      visitsToCancel.push(...visits.map((v: any) => v.id));

      // Also get active episodes to deactivate
      const episodes = data?.episode || [];
      const activeEpisodes = episodes.filter((e: any) => e.active);
      episodesToDeactivate.push(...activeEpisodes.map((e: any) => e.id));
    }

    if (visitsToCancel.length === 0 && episodesToDeactivate.length === 0) {
      onProgress?.('No visits or episodes to clean up');
      return;
    }

    if (visitsToCancel.length > 0) {
      onProgress?.(`Canceling ${visitsToCancel.length} visits...`);

      // Cancel all visits
      await Promise.allSettled(
        visitsToCancel.map((visitId) =>
          apolloClient.mutate({
            mutation: CANCEL_VISIT_MUTATION,
            variables: { visitId, visitCancellationReasonId: cancellationReasonId },
          })
        )
      );

      onProgress?.(`✓ Canceled ${visitsToCancel.length} visits`);
    }

    // Deactivate episodes so patients don't show up in activePatients query
    if (episodesToDeactivate.length > 0) {
      onProgress?.(`Deactivating ${episodesToDeactivate.length} episodes...`);

      await Promise.allSettled(
        episodesToDeactivate.map((episodeId) =>
          apolloClient.mutate({
            mutation: UPDATE_EPISODE_MUTATION,
            variables: {
              episodeId,
              active: false,
            },
          })
        )
      );

      onProgress?.(`✓ Deactivated ${episodesToDeactivate.length} episodes`);
    }
  } catch (error: any) {
    console.error('Failed to cancel visits for patients:', error);
    throw new Error(`Failed to cancel visits: ${error.message}`);
  }
}
